import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const ITEMS_PER_PAGE = 20;

const useController = ({ games, totalCount, loading, loadMoreItems }) => {
	const loader = useRef(null);
	const observerRef = useRef(null);
	const [visibleItems, setVisibleItems] = useState(ITEMS_PER_PAGE);

	const filteredGames = useMemo(() => {
		if (!games || games.length === 0) return [];
		return games.filter((game) => game.affiliate !== "egt");
	}, [games]);

	const canLoadMore = useMemo(() => {
		return !loading && games.length < totalCount;
	}, [loading, games.length, totalCount]);

	const visibleGames = useMemo(() => {
		return filteredGames.slice(0, visibleItems);
	}, [filteredGames, visibleItems]);

	const handleObserver = useCallback(
		(entities) => {
			const target = entities[0];
			if (target.isIntersecting && canLoadMore) {
				const wrapperMain = document.querySelector(".wrapper-main");
				if (wrapperMain && wrapperMain.scrollHeight > wrapperMain.clientHeight) {
					loadMoreItems();
				}
			}
		},
		[canLoadMore, loadMoreItems]
	);

	useEffect(() => {
		if (observerRef.current) {
			observerRef.current.disconnect();
		}

		observerRef.current = new IntersectionObserver(handleObserver, {
			rootMargin: "1000px 0px",
			threshold: 0.1,
		});

		if (loader.current) {
			observerRef.current.observe(loader.current);
		}

		return () => {
			if (observerRef.current) {
				observerRef.current.disconnect();
			}
		};
	}, [handleObserver]);

	useEffect(() => {
		if (filteredGames.length > visibleItems) {
			setVisibleItems((prev) => Math.min(prev + ITEMS_PER_PAGE, filteredGames.length));
		}
	}, [filteredGames.length, visibleItems]);

	return {
		refs: { loader },
		state: {
			canLoadMore,
			visibleGames,
		},
	};
};

export default useController;
